import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import { logger } from '../../../utils/logger';

interface AuthInitializationState {
  isInitialized: boolean;
  isInitializing: boolean;
  error: string | null;
}

// Global state to prevent multiple initializations
let globalInitState = {
  isInitialized: false,
  isInitializing: false,
  initPromise: null as Promise<any> | null
};

/**
 * Simplified auth initialization hook - NO RACE CONDITIONS
 * Uses sequential execution and single source of truth
 */
export const useAuthInitialization = () => {
  const dispatch: AppDispatch = useDispatch();
  const { isAuthenticated, isLoading: reduxIsLoading, isInitialized: reduxIsInitialized } = useSelector((state: RootState) => state.auth);
  const [state, setState] = useState<AuthInitializationState>({
    isInitialized: globalInitState.isInitialized,
    isInitializing: globalInitState.isInitializing,
    error: null
  });
  
  const initializationAttempted = useRef(false);

  // Sync local state with Redux state to ensure consistency
  useEffect(() => {
    if (reduxIsInitialized && !state.isInitialized) {
      logger.auth('Syncing local state with Redux: initialization complete');
      globalInitState.isInitialized = true;
      globalInitState.isInitializing = false;
      setState({
        isInitialized: true,
        isInitializing: false,
        error: null
      });
    }
    
    // If Redux shows not loading and we're still showing initializing, sync the state
    if (!reduxIsLoading && state.isInitializing) {
      logger.auth('Syncing local state with Redux: loading complete');
      globalInitState.isInitializing = false;
      setState(prev => ({
        ...prev,
        isInitializing: false
      }));
    }
  }, [reduxIsInitialized, reduxIsLoading, state.isInitialized, state.isInitializing]);

  useEffect(() => {
    // Only initialize once per app lifecycle
    if (initializationAttempted.current || globalInitState.isInitialized || globalInitState.isInitializing) {
      // If already initialized, sync local state with global state
      if (globalInitState.isInitialized && !state.isInitialized) {
        setState({
          isInitialized: true,
          isInitializing: false,
          error: null
        });
      }
      return;
    }

    initializationAttempted.current = true;
    
    const initializeAuth = async () => {
      try {
        // DON'T set isInitializing to true yet - wait to see if we have accounts first
        logger.auth('Starting simplified auth initialization...');
        
        // Create a single initialization promise to prevent race conditions
        if (!globalInitState.initPromise) {
          globalInitState.initPromise = initializeSequentially(setState);
        }
        
        await globalInitState.initPromise;
        
        // Mark as initialized and ensure isInitializing is false
        globalInitState.isInitialized = true;
        globalInitState.isInitializing = false;
        setState({
          isInitialized: true,
          isInitializing: false,
          error: null
        });
        
        logger.auth('Simplified auth initialization completed', {
          isInitialized: true,
          isInitializing: false
        });
        
      } catch (error) {
        logger.error('Simplified auth initialization failed:', error);
        globalInitState.isInitializing = false;
        globalInitState.initPromise = null;
        setState({
          isInitialized: false,
          isInitializing: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    };

    initializeAuth();
  }, [dispatch, state.isInitialized]);

  // Listen for account changes (but only update local state, don't re-initialize)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key?.startsWith('smartmatrix_account_') || 
          e.key === 'smartmatrix_accounts_list' ||
          e.key === 'smartmatrix_active_account') {
        logger.auth('Storage changed, resetting auth initialization...');
        
        // Reset global state to allow re-initialization
        globalInitState.isInitialized = false;
        globalInitState.isInitializing = false;
        globalInitState.initPromise = null;
        initializationAttempted.current = false;
        
        setState({
          isInitialized: false,
          isInitializing: false,
          error: null
        });
      }
    };

    const handleAccountChange = () => {
      logger.auth('Account change event received, resetting auth initialization...');
      
      // Reset global state to allow re-initialization
      globalInitState.isInitialized = false;
      globalInitState.isInitializing = false;
      globalInitState.initPromise = null;
      initializationAttempted.current = false;
      
      setState({
        isInitialized: false,
        isInitializing: false,
        error: null
      });
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('smartmatrix:account-added', handleAccountChange);
    window.addEventListener('smartmatrix:account-switched', handleAccountChange);
    window.addEventListener('smartmatrix:account-removed', handleAccountChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('smartmatrix:account-added', handleAccountChange);
      window.removeEventListener('smartmatrix:account-switched', handleAccountChange);
      window.removeEventListener('smartmatrix:account-removed', handleAccountChange);
    };
  }, []);

  return {
    ...state,
    isAuthenticated
  };
};

/**
 * Sequential initialization - no race conditions
 * Now uses authManager.initializeSession() for proper token refresh handling
 * Only shows loading spinner if we have an account to initialize
 */
async function initializeSequentially(setState: React.Dispatch<React.SetStateAction<AuthInitializationState>>): Promise<void> {
  try {
    // Step 1: Initialize multi-account system (fast - just reads from localStorage)
    logger.auth('Sequential Init Step 1: Initializing multi-account system...');
    const { initializeMultiAccountThunk } = await import('../../../store/thunks/multiAccountThunks');
    const store = (await import('../../../store')).default;
    const result = await store.dispatch(initializeMultiAccountThunk());
    
    if (initializeMultiAccountThunk.fulfilled.match(result)) {
      const { accounts, activeAccount } = result.payload;
      logger.auth('Sequential Init Step 1 Complete: Multi-account system initialized', {
        accountCount: accounts.length,
        hasActiveAccount: !!activeAccount
      });
      
      // Step 2: Use authManager.initializeSession() if we have an active account
      // This properly handles token refresh and fetches real user data from API
      if (activeAccount?.secret) {
        // NOW set isInitializing to true since we have an account to initialize
        globalInitState.isInitializing = true;
        setState(prev => ({ ...prev, isInitializing: true, error: null }));
        
        logger.auth('Sequential Init Step 2: Calling authManager.initializeSession()...');
        
        const { authManager } = await import('../../../services/auth/authManager');
        const initResult = await authManager.initializeSession();
        
        if (initResult.success) {
          logger.auth('Sequential Init Step 2 Complete: Session initialized successfully');
          // Reset isInitializing after successful initialization
          globalInitState.isInitializing = false;
          setState(prev => ({ ...prev, isInitializing: false }));
        } else {
          // Token refresh failed or session is invalid - stop here and redirect to login
          logger.error('Session initialization failed, redirecting to login...', { error: initResult.error });
          
          // Reset isInitializing before throwing
          globalInitState.isInitializing = false;
          setState(prev => ({ ...prev, isInitializing: false }));
          
          // authManager.initializeSession() already handles:
          // 1. Clearing tokens
          // 2. Dispatching initializationFailure
          // 3. Redirecting to login page
          // So we just need to throw to stop the initialization flow
          throw new Error(initResult.error || 'Session initialization failed');
        }
      } else {
        logger.auth('No active account found, skipping session initialization');
        // CRITICAL: Don't set isInitializing to true - we're done immediately
        // Ensure global state reflects this
        globalInitState.isInitializing = false;
        setState(prev => ({ ...prev, isInitializing: false }));
      }
    } else {
      throw new Error(String(result.payload) || 'Multi-account initialization failed');
    }
    
  } catch (error) {
    logger.error('Sequential initialization failed:', error);
    // Ensure isInitializing is false on error
    globalInitState.isInitializing = false;
    setState(prev => ({ ...prev, isInitializing: false }));
    throw error;
  }
}

/**
 * Reset the global initialization state (for testing or manual reset)
 */
export const resetAuthInitialization = () => {
  globalInitState.isInitialized = false;
  globalInitState.isInitializing = false;
  globalInitState.initPromise = null;
};